
import * as React from "react";
import { Suspense } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { ThemeProvider } from "@/components/theme-provider";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { FeatureFlagProvider } from "@/contexts/FeatureFlagContext";
import { EntitlementProvider } from "@/contexts/EntitlementContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import SecurityHeaders from "@/components/SecurityHeaders";
import LEDBackground from "@/components/LEDBackground";
import CookieConsent from "./components/CookieConsent";
import SessionLoggerInit from "./components/SessionLoggerInit";
import GlossaryPanel from "@/components/help/GlossaryPanel";
import { ConfirmProvider } from "@/hooks/useConfirm";
import RouteSkeleton from "@/components/RouteSkeleton";
import CanonicalGuard from "@/components/CanonicalGuard";


// Route-level pages — lazy loaded so visitors only download what they navigate to.
// Index is eagerly imported so the default landing page renders without a Suspense flash.
import Index from "./pages/Index";
const BrandSelection = lazy(() => import("./pages/BrandSelection"));
const Projects = lazy(() => import("./pages/Projects"));
const Kit = lazy(() => import("./pages/Kit"));
const AccessoriesBrands = lazy(() =>
  import("./pages/Accessories").then((m) => ({ default: m.AccessoriesBrands }))
);
const AccessoryCategories = lazy(() =>
  import("./pages/Accessories").then((m) => ({ default: m.AccessoryCategories }))
);
const AccessoryDevices = lazy(() =>
  import("./pages/Accessories").then((m) => ({ default: m.AccessoryDevices }))
);
const AccessoryDetail = lazy(() =>
  import("./pages/Accessories").then((m) => ({ default: m.AccessoryDetail }))
);
const PanelSeriesSelection = lazy(() => import("./pages/PanelSeriesSelection"));

const Configurator = lazy(() => import("./pages/Configurator"));
import RouteErrorBoundary from "./components/RouteErrorBoundary";
const Configure = lazy(() => import("./pages/Configure"));
const ComplexLayoutBuilder = lazy(() => import("./pages/ComplexLayoutBuilder"));
const CustomPanelPage = lazy(() => import("./pages/CustomPanelPage"));
const Admin = lazy(() => import("./pages/Admin"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

const NotFound = lazy(() => import("./pages/NotFound"));
const Visualize = lazy(() => import("./pages/Visualize"));
const Visualiser = lazy(() => import("./pages/Visualiser"));
const Help = lazy(() => import("./pages/Help"));
const SharedConfiguration = lazy(() => import("./pages/SharedConfiguration"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Accessibility = lazy(() => import("./pages/Accessibility"));
const Attribution = lazy(() => import("./pages/Attribution"));
const DPIA = lazy(() => import("./pages/DPIA"));
const PendingApproval = lazy(() => import("./pages/PendingApproval"));
const Account = lazy(() => import("./pages/Account"));
const Billing = lazy(() => import("./pages/Billing"));
const Settings = lazy(() => import("./pages/Settings"));
const Pricing = lazy(() => import("./pages/Pricing"));
const SecurityHeadersAudit = lazy(() => import("./pages/SecurityHeadersAudit"));
const MetaTagsAudit = lazy(() => import("./pages/MetaTagsAudit"));
const Changelog = lazy(() => import("./pages/Changelog"));
const AdminChangelog = lazy(() => import("./pages/AdminChangelog"));
const TechnicalReference = lazy(() => import("./pages/TechnicalReference"));
const PublicCompare = lazy(() => import("./pages/PublicCompare"));
const Support = lazy(() => import("./pages/Support"));
const ThermalCalculator = lazy(() => import("./pages/ThermalCalculator"));
const PanelPopout = lazy(() => import("./pages/PanelPopout"));
const RackBuilder = lazy(() => import("./pages/RackBuilder"));
const Checkout = lazy(() => import("./pages/Checkout"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const SubscriptionCancelled = lazy(() => import("./pages/SubscriptionCancelled"));
const Troubleshooting = lazy(() => import("./pages/Troubleshooting"));
const Team = lazy(() => import("./pages/Team"));
const InviteAccept = lazy(() => import("./pages/InviteAccept"));


// Minimal fallback — a small centered spinner over a transparent background so
// the LEDBackground keeps showing through and route transitions don't flash.
const RouteFallback = () => <RouteSkeleton />;

// No eager warm-import of the Configurator / Configure chunks: they used to be
// pulled in on idle from every page (including "/"), which downloaded the whole
// configurator, report and PDF code before a visitor did anything. Those chunks
// now load on route entry, or on hover/focus of "Open" on a project card
// (see prefetchConfigure in src/lib/routePrefetch.ts).

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <FeatureFlagProvider>
          <EntitlementProvider>
            <TooltipProvider>
              <ConfirmProvider>
              <SecurityHeaders />
              <LEDBackground />
              <Toaster />
              <Sonner />
              <CookieConsent />
              <BrowserRouter>
                <SessionLoggerInit />
                <CanonicalGuard />
                <GlossaryPanel />

                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/panel-popout" element={<PanelPopout />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/reset-password" element={<ResetPassword />} />

                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/accessibility" element={<Accessibility />} />
                    <Route path="/attribution" element={<Attribution />} />
                    <Route path="/dpia" element={<DPIA />} />
                    <Route path="/shared/:token" element={<SharedConfiguration />} />
                    <Route path="/help" element={<Help />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/security-headers" element={<ProtectedRoute requireAdmin><SecurityHeadersAudit /></ProtectedRoute>} />
                    <Route path="/meta-tags" element={<ProtectedRoute requireAdmin><MetaTagsAudit /></ProtectedRoute>} />
                    <Route path="/changelog" element={<Changelog />} />
                    <Route path="/admin/changelog" element={<ProtectedRoute requireAdmin><AdminChangelog /></ProtectedRoute>} />
                    <Route path="/reference" element={<TechnicalReference />} />
                    <Route path="/technical-reference" element={<Navigate to="/reference" replace />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="/compare" element={<PublicCompare initialTab="compare" />} />
                    <Route path="/pitch-compare" element={<PublicCompare initialTab="pitch-compare" />} />
                    <Route path="/thermal" element={<ThermalCalculator />} />
                    <Route path="/troubleshooting" element={<Troubleshooting />} />
                    <Route path="/troubleshooting/:symptomId" element={<Troubleshooting />} />
                    <Route path="/panel-recommender" element={<Navigate to="/" replace />} />
                    {/* Legacy path — the feature is no longer called "AI". */}
                    <Route path="/ai-assistant" element={<Navigate to="/" replace />} />
                    <Route path="/checkout/return" element={<CheckoutReturn />} />
                    <Route path="/subscription/success" element={<ProtectedRoute requireApproved={false}><SubscriptionSuccess /></ProtectedRoute>} />
                    <Route path="/subscription/cancelled" element={<SubscriptionCancelled />} />
                    <Route path="/checkout/:priceId" element={<ProtectedRoute requireApproved={false}><Checkout /></ProtectedRoute>} />

                    {/* Pending-approval landing (signed in but not yet approved) */}
                    <Route
                      path="/pending-approval"
                      element={
                        <ProtectedRoute requireApproved={false}>
                          <PendingApproval />
                        </ProtectedRoute>
                      }
                    />

                    {/* Approved-only routes */}
                    <Route path="/" element={<Index />} />
                    <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
                    <Route path="/kit" element={<ProtectedRoute><Kit /></ProtectedRoute>} />
                    <Route path="/brands" element={<ProtectedRoute><BrandSelection /></ProtectedRoute>} />
                    <Route path="/brands/favorites" element={<Navigate to="/brands" replace />} />
                    <Route path="/brands/:brandId/panels" element={<ProtectedRoute><PanelSeriesSelection /></ProtectedRoute>} />
                    <Route path="/panels" element={<ProtectedRoute><BrandSelection /></ProtectedRoute>} />
                    <Route path="/accessories" element={<ProtectedRoute><AccessoriesBrands /></ProtectedRoute>} />
                    <Route path="/accessories/:brandId" element={<ProtectedRoute><AccessoryCategories /></ProtectedRoute>} />
                    <Route path="/accessories/:brandId/:category" element={<ProtectedRoute><AccessoryDevices /></ProtectedRoute>} />
                    <Route path="/accessories/:brandId/:category/:deviceId" element={<ProtectedRoute><AccessoryDetail /></ProtectedRoute>} />
                    <Route path="/configurator" element={<ProtectedRoute><RouteErrorBoundary><Configurator /></RouteErrorBoundary></ProtectedRoute>} />
                    <Route path="/configurator/custom" element={<CustomPanelPage />} />
                    <Route path="/configure/:panelId" element={<ProtectedRoute><Configure /></ProtectedRoute>} />
                    <Route path="/configure/:panelId/complex-layout" element={<ProtectedRoute><ComplexLayoutBuilder /></ProtectedRoute>} />
                    <Route path="/visualize" element={<ProtectedRoute requireBeta><Visualize /></ProtectedRoute>} />
                    <Route path="/visualiser" element={<ProtectedRoute requireBeta><Visualiser /></ProtectedRoute>} />
                    <Route path="/racks" element={<ProtectedRoute requireBeta><RackBuilder /></ProtectedRoute>} />
                    <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                    <Route path="/billing" element={<ProtectedRoute requireApproved={false}><Billing /></ProtectedRoute>} />
                    <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                    <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
                    <Route path="/invite/:token" element={<InviteAccept />} />



                    <Route path="/admin" element={
                      <ProtectedRoute requireAdmin>
                        <Admin />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/categorize" element={
                      <ProtectedRoute requireAdmin>
                        <Admin />
                      </ProtectedRoute>
                    } />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
              </ConfirmProvider>
            </TooltipProvider>
          </EntitlementProvider>
        </FeatureFlagProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
